import React, { useMemo, useState } from 'react';
import Icon from '@/components/AppIcon';
import Button from '@/components/ui/Button';
import Select from '@/components/ui/Select';
import Input from '@/components/ui/Input';
import useTranslation from '@/hooks/useTranslation';
import formatTranslation from '@/utils/formatTranslation';
import { LANGUAGE_TO_LOCALE } from '../constants';

const ScheduleFilters = ({ 
  locations,
  selectedLocation,
  onLocationChange,
  departments,
  selectedDepartment,
  onDepartmentChange,
  viewMode,
  onViewModeChange,
  onRefresh,
  totalShifts,
  totalHours,
  laborCost
}) => {
  const { t, language } = useTranslation(['employeeScheduling']);
  const locale = LANGUAGE_TO_LOCALE[language] ?? LANGUAGE_TO_LOCALE.en;
  const currencyFormatter = useMemo(
    () => new Intl.NumberFormat(locale, { style: 'currency', currency: 'USD' }),
    [locale],
  );
  const laborCostDisplay = useMemo(
    () => currencyFormatter.format(laborCost ?? 0),
    [currencyFormatter, laborCost],
  );

  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [dateRange, setDateRange] = useState('week');
  const [shiftStatus, setShiftStatus] = useState('all');

  const locationOptions = locations?.map(location => ({
    value: location?.id,
    label: location?.name,
    description: location?.address
  }));

  const departmentOptions = useMemo(
    () => [
      { value: 'all', label: t('employeeScheduling.filters.departmentAll') },
      ...(departments?.map((dept) => ({
        value: dept?.id,
        label: dept?.name,
      })) ?? []),
    ],
    [departments, t],
  );

  const viewModeOptions = useMemo(
    () => [
      { value: 'day', label: t('employeeScheduling.filters.viewModes.day') },
      { value: 'week', label: t('employeeScheduling.filters.viewModes.week') },
      { value: 'month', label: t('employeeScheduling.filters.viewModes.month') },
    ],
    [t],
  );

  const dateRangeOptions = useMemo(
    () => [
      { value: 'today', label: t('employeeScheduling.filters.dateRanges.today') },
      { value: 'week', label: t('employeeScheduling.filters.dateRanges.week') },
      { value: 'month', label: t('employeeScheduling.filters.dateRanges.month') },
      { value: 'custom', label: t('employeeScheduling.filters.dateRanges.custom') },
    ],
    [t],
  );

  const shiftStatusOptions = useMemo(
    () => [
      { value: 'all', label: t('employeeScheduling.filters.shiftStatuses.all') },
      { value: 'scheduled', label: t('employeeScheduling.filters.shiftStatuses.scheduled') },
      { value: 'published', label: t('employeeScheduling.filters.shiftStatuses.published') },
      { value: 'draft', label: t('employeeScheduling.filters.shiftStatuses.draft') },
      { value: 'conflicts', label: t('employeeScheduling.filters.shiftStatuses.conflicts') },
    ],
    [t],
  );

  const handleLocationSelect = (locationId) => {
    const location = locations?.find(loc => loc?.id === locationId);
    if (location) {
      onLocationChange(location);
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg shadow-elevation-1 mb-6">
      {/* Main Filters Row */}
      <div className="p-4">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
          {/* Left Side - Primary Filters */}
          <div className="flex flex-col sm:flex-row sm:items-center space-y-3 sm:space-y-0 sm:space-x-4">
            {/* Location Selector */}
            <div className="min-w-64">
              <Select
                label={t('employeeScheduling.filters.labels.location')}
                options={locationOptions}
                value={selectedLocation?.id}
                onChange={handleLocationSelect}
                searchable
              />
            </div>

            {/* Department Filter */}
            <div className="min-w-48">
              <Select
                label={t('employeeScheduling.filters.labels.department')}
                options={departmentOptions}
                value={selectedDepartment}
                onChange={onDepartmentChange}
              />
            </div>

            {/* View Mode */}
            <div className="min-w-40">
              <Select
                label={t('employeeScheduling.filters.labels.view')}
                options={viewModeOptions}
                value={viewMode}
                onChange={onViewModeChange}
              />
            </div>
          </div>

          {/* Right Side - Actions */}
          <div className="flex items-center space-x-3">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
              iconName={showAdvancedFilters ? 'ChevronUp' : 'ChevronDown'}
              iconPosition="right"
              iconSize={16}
            >
              {t('employeeScheduling.filters.buttons.filters')}
            </Button>
            
            <Button
              variant="outline"
              size="sm"
              onClick={onRefresh}
              iconName="RotateCcw"
              iconSize={16}
            >
              {t('employeeScheduling.filters.buttons.refresh')}
            </Button>

            <div className="hidden lg:block w-px h-6 bg-border" />

            {/* Quick Stats */}
            <div className="hidden lg:flex items-center space-x-4 text-sm">
              <div className="flex items-center space-x-1 text-muted-foreground">
                <Icon name="Calendar" size={14} />
                <span>
                  {formatTranslation(t('employeeScheduling.filters.stats.shifts'), {
                    count: totalShifts ?? 0,
                  })}
                </span>
              </div>
              <div className="flex items-center space-x-1 text-muted-foreground">
                <Icon name="Clock" size={14} />
                <span>
                  {formatTranslation(t('employeeScheduling.filters.stats.hours'), {
                    hours: totalHours ?? 0,
                  })}
                </span>
              </div>
              <div className="flex items-center space-x-1 text-muted-foreground">
                <Icon name="DollarSign" size={14} />
                <span>
                  {formatTranslation(t('employeeScheduling.filters.stats.labor'), {
                    amount: laborCostDisplay,
                  })}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Advanced Filters */}
        {showAdvancedFilters && (
          <div className="mt-4 pt-4 border-t border-border">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <Select
                label={t('employeeScheduling.filters.labels.dateRange')}
                options={dateRangeOptions}
                value={dateRange}
                onChange={setDateRange}
              />

              <Select
                label={t('employeeScheduling.filters.labels.shiftStatus')}
                options={shiftStatusOptions}
                value={shiftStatus}
                onChange={setShiftStatus}
              />

              <Input
                label={t('employeeScheduling.filters.labels.minHours')}
                type="number"
                placeholder="0"
                min="0"
                max="24"
              />

              <Input
                label={t('employeeScheduling.filters.labels.maxHours')}
                type="number"
                placeholder="24"
                min="0"
                max="24"
              />
            </div>

            <div className="flex items-center justify-between mt-4">
              <div className="flex items-center space-x-4">
                <label className="flex items-center space-x-2 text-sm">
                  <input
                    type="checkbox"
                    className="rounded border-border text-primary focus:ring-primary"
                  />
                  <span>{t('employeeScheduling.filters.advanced.showConflicts')}</span>
                </label>
                <label className="flex items-center space-x-2 text-sm">
                  <input
                    type="checkbox"
                    className="rounded border-border text-primary focus:ring-primary"
                  />
                  <span>{t('employeeScheduling.filters.advanced.hidePublished')}</span>
                </label>
              </div>

              <div className="flex items-center space-x-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setDateRange('week');
                    setShiftStatus('all');
                  }}
                >
                  {t('employeeScheduling.filters.buttons.clear')}
                </Button>
                <Button
                  variant="default"
                  size="sm"
                  iconName="Search"
                  iconSize={16}
                >
                  {t('employeeScheduling.filters.buttons.apply')}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
      {/* Mobile Stats */}
      <div className="lg:hidden px-4 pb-4">
        <div className="flex items-center justify-around text-sm bg-muted/20 rounded-lg p-3">
          <div className="text-center">
            <div className="font-semibold text-foreground">{totalShifts}</div>
            <div className="text-xs text-muted-foreground">
              {t('employeeScheduling.filters.mobile.shifts')}
            </div>
          </div>
          <div className="text-center">
            <div className="font-semibold text-foreground">{totalHours}h</div>
            <div className="text-xs text-muted-foreground">
              {t('employeeScheduling.filters.mobile.hours')}
            </div>
          </div>
          <div className="text-center">
            <div className="font-semibold text-foreground">{laborCostDisplay}</div>
            <div className="text-xs text-muted-foreground">
              {t('employeeScheduling.filters.mobile.labor')}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ScheduleFilters;
